/**
 * Go Statistics Service
 *
 * Records game results via StatisticsManager, tracks per-difficulty
 * and multiplayer stats.
 * Follows ReversiStatisticsService pattern.
 */

import {
  GoStatistics,
  GoGameResult,
  GoGameStats,
  GoDifficulty,
  GoGameSettings,
  GoColor,
} from '../types';
import { StatisticsManager } from '../../services/StatisticsManager';

export class GoStatisticsService {
  private statsManager: StatisticsManager;
  private readonly STATS_KEY = 'go_statistics';

  constructor(statsManager: StatisticsManager) {
    this.statsManager = statsManager;
  }

  private createEmptyStats(): GoStatistics {
    return {
      singlePlayer: {
        easy: this.createEmptyGameStats(),
        medium: this.createEmptyGameStats(),
        hard: this.createEmptyGameStats(),
      },
      localMultiplayer: this.createEmptyGameStats(),
      onlineMultiplayer: this.createEmptyGameStats(),
      totalGames: 0,
      totalPlayTime: 0,
    };
  }

  private createEmptyGameStats(): GoGameStats {
    return {
      wins: 0,
      losses: 0,
      draws: 0,
      winPercentage: 0,
      averageScore: 0,
      highestScore: 0,
      totalCaptures: 0,
    };
  }

  getStatistics(userId?: string): GoStatistics {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      const data = localStorage.getItem(key);
      return data ? (JSON.parse(data) as GoStatistics) : this.createEmptyStats();
    } catch (error) {
      console.error('Failed to get go statistics:', error);
      return this.createEmptyStats();
    }
  }

  recordGameResult(
    result: GoGameResult,
    playerColor: GoColor,
    userId?: string
  ): boolean {
    try {
      const stats = this.getStatistics(userId);

      if (result.mode === 'singleplayer' && result.difficulty) {
        this.updateGameStats(stats.singlePlayer[result.difficulty], result, playerColor);
      } else if (result.mode === 'local-multiplayer') {
        this.updateGameStats(stats.localMultiplayer, result, playerColor);
      } else if (result.mode === 'multiplayer-realtime') {
        this.updateGameStats(stats.onlineMultiplayer, result, playerColor);
      }

      stats.totalGames++;
      stats.totalPlayTime += result.duration;

      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(stats));
      return true;
    } catch (error) {
      console.error('Failed to record go game result:', error);
      return false;
    }
  }

  private updateGameStats(
    stats: GoGameStats,
    result: GoGameResult,
    playerColor: GoColor
  ): void {
    const totalGames = stats.wins + stats.losses + stats.draws;

    if (result.winner === 'draw') {
      stats.draws++;
    } else if (result.winner === playerColor) {
      stats.wins++;
    } else {
      stats.losses++;
    }

    const newTotalGames = stats.wins + stats.losses + stats.draws;
    stats.winPercentage = newTotalGames > 0 ? (stats.wins / newTotalGames) * 100 : 0;

    const playerScore = result.score[playerColor];
    stats.averageScore =
      newTotalGames > 0
        ? (stats.averageScore * totalGames + playerScore) / newTotalGames
        : playerScore;

    if (playerScore > stats.highestScore) {
      stats.highestScore = playerScore;
    }

    stats.totalCaptures += result.totalCaptures;
  }

  getModeStatistics(
    mode: GoGameSettings['mode'],
    difficulty?: GoDifficulty,
    userId?: string
  ): GoGameStats {
    const stats = this.getStatistics(userId);

    if (mode === 'singleplayer' && difficulty) {
      return stats.singlePlayer[difficulty];
    } else if (mode === 'local-multiplayer') {
      return stats.localMultiplayer;
    } else if (mode === 'multiplayer-realtime') {
      return stats.onlineMultiplayer;
    }

    return this.createEmptyGameStats();
  }

  resetStatistics(userId?: string): boolean {
    try {
      const key = userId ? `${this.STATS_KEY}_${userId}` : this.STATS_KEY;
      localStorage.setItem(key, JSON.stringify(this.createEmptyStats()));
      return true;
    } catch (error) {
      console.error('Failed to reset go statistics:', error);
      return false;
    }
  }
}
